// src/utils/canonicalBOM.ts
// ─────────────────────────────────────────────────────────────────
// THE canonical Bill of Materials for the MCBCEK platform.
//
// Design intent (Phase 1):
//   All three previous BOM concepts (Job.bomLines, Drawings MOCK_BOM,
//   AICompliance MOCK_BOM_RESULTS) are retired in favour of this single
//   type.  Every module — Drawings AI extraction, WorkInstructions,
//   MobileComms, RFQ, PurchaseOrders, AICompliance, Jobs — reads from
//   and writes to this module.
//
// Persistence: localStorage (same pattern as vehicleAssignments.ts)
//
// Pooling & returns (per 4.6 clarification):
//   - Jobs procure their own materials but may pool surplus across jobs.
//   - Excess materials from site can be returned to the warehouse.
//   - ALL pooled or returned materials still require compliance
//     verification against the receiving job's client specs — this
//     is a hard requirement and is enforced by the type + UI.
// ─────────────────────────────────────────────────────────────────

export type BOMSource =
  | 'AI_EXTRACTED'    // AI read this from a drawing / spec document
  | 'MANUAL'          // PM/PC typed this in manually
  | 'VARIATION'       // created when a VAR was approved
  | 'POOLED_IN'       // transferred in from another job's surplus
  | 'RETURNED'        // returned from site to warehouse then reallocated here

export type BOMComplianceFlag = 'COMPLIANT' | 'NEEDS_REVIEW' | 'NON_COMPLIANT' | 'PENDING'

export type BOMProcurementStatus =
  | 'NOT_ORDERED'
  | 'PENDING_APPROVAL'
  | 'ON_ORDER'
  | 'PARTIAL_RECEIVED'
  | 'DELIVERED'

export type BOMStockStatus =
  | 'ON_SITE'
  | 'PARTIAL_ON_SITE'
  | 'WAREHOUSE'       // physically in company warehouse
  | 'IN_TRANSIT'      // ordered & shipped, not yet confirmed received
  | 'NOT_AVAILABLE'

export type BOMMaterialType = 'BUILD_MATERIAL' | 'CONSUMABLE' | 'PLANT' | 'SUBCONTRACT'

export interface CanonicalBOMLine {
  id: string
  jobId: string
  jobNumber: string

  // ── Core description ────────────────────────────────────────
  code: string                  // catalogue SKU / internal code
  description: string
  materialType: BOMMaterialType
  qty: number
  unit: string
  unitCost: number
  total: number                 // qty × unitCost

  // ── Origin tracing ──────────────────────────────────────────
  source: BOMSource
  sourceDocId?: string | null   // DrawingDocument.id
  sourcePageRef?: string | null
  drawingTaskId?: string | null // DrawingTask.id — REAL FK (Phase 1 key change)
  variationId?: string | null   // VariationRequest.id if from approved VAR
  pooledFromJobId?: string | null  // source job if POOLED_IN

  // ── Compliance (CRITICAL — required for all materials including pooled) ──
  complianceFlag: BOMComplianceFlag
  complianceNote?: string | null
  specRef?: string | null       // e.g. 'QD-TSG-0026 Rev B cl. 5.1'
  complianceCheckedAt?: string | null
  complianceCheckedBy?: string | null
  // If pooled/returned, must re-verify against the receiving job's specs
  complianceVerifiedForThisJob: boolean

  // ── Procurement ─────────────────────────────────────────────
  procurementStatus: BOMProcurementStatus
  poNumber?: string | null
  rfqLineId?: string | null     // RFQLineItem.id — real FK (Phase 1)

  // ── Stock / receipt ─────────────────────────────────────────
  stockStatus: BOMStockStatus
  receivedQty?: number          // from GRN(s)
  grnIds?: string[]             // GoodsReceivedNote.id references

  // ── Warehouse returns ────────────────────────────────────────
  warehouseReturnable: boolean  // can site excess go back to warehouse?
  returnedQty?: number
  returnedAt?: string | null
  returnedByName?: string | null
  returnNote?: string | null

  // ── Discipline / WHS ────────────────────────────────────────
  discipline?: string
  whsFlags?: string[]           // ['WORKING_HEIGHTS', 'CONFINED_SPACE', …]

  // ── Meta ────────────────────────────────────────────────────
  createdAt: string
  updatedAt: string
  createdByName: string
}

// ── Storage keys ────────────────────────────────────────────────
const STORE_KEY = 'tp_canonical_bom_lines'

function canStore() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readAll(): CanonicalBOMLine[] {
  if (!canStore()) return []
  try {
    const raw = window.localStorage.getItem(STORE_KEY)
    return raw ? (JSON.parse(raw) as CanonicalBOMLine[]) : []
  } catch { return [] }
}

function writeAll(lines: CanonicalBOMLine[]) {
  if (!canStore()) return
  window.localStorage.setItem(STORE_KEY, JSON.stringify(lines))
}

// ── Seed data ────────────────────────────────────────────────────
// Mirrors Job.bomLines from mockData.ts but in canonical form,
// plus BOMItems from mockDataDrawings.ts, merged and de-duped.
const SEED: CanonicalBOMLine[] = [
  // ── Job j1 — Kessels Rd ──────────────────────────────────────
  {
    id: 'cbom_j1_001', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'LED-SIG-300-RGB', description: 'LED Signal Head 300mm Tri-colour (Red/Amber/Green)',
    materialType: 'BUILD_MATERIAL', qty: 12, unit: 'EA', unitCost: 487.50, total: 5850,
    source: 'AI_EXTRACTED', sourceDocId: 'doc001', sourcePageRef: 'Sheet 9 — Signal Head Schedule',
    drawingTaskId: 'tsk014', variationId: null, pooledFromJobId: null,
    complianceFlag: 'COMPLIANT', complianceNote: null, specRef: 'QD-TSG-0026 Rev B cl. 5.1',
    complianceCheckedAt: '2026-05-15T09:08:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'ON_ORDER', poNumber: 'PO-0051', rfqLineId: null,
    stockStatus: 'IN_TRANSIT', receivedQty: 0, grnIds: [],
    warehouseReturnable: true, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Electrical', whsFlags: ['WORKING_HEIGHTS'],
    createdAt: '2026-05-15T09:10:00Z', updatedAt: '2026-05-15T09:10:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j1_002', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'POLE-SIG-8M-76', description: 'Signal Pole Galvanised Steel 8m (76mm OD)',
    materialType: 'BUILD_MATERIAL', qty: 4, unit: 'EA', unitCost: 1240.00, total: 4960,
    source: 'AI_EXTRACTED', sourceDocId: 'doc002', sourcePageRef: 'Sheet 2 — Foundation Details',
    drawingTaskId: 'tsk010', variationId: null, pooledFromJobId: null,
    complianceFlag: 'COMPLIANT', complianceNote: null, specRef: 'MRTS50 cl. 8.2',
    complianceCheckedAt: '2026-05-15T09:11:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'DELIVERED', poNumber: 'PO-0051', rfqLineId: null,
    stockStatus: 'ON_SITE', receivedQty: 4, grnIds: ['grn001'],
    warehouseReturnable: false, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Electrical', whsFlags: ['WORKING_HEIGHTS'],
    createdAt: '2026-05-15T09:11:00Z', updatedAt: '2026-05-15T09:11:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j1_003', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'UPVC-33MD', description: 'UPVC Conduit 33mm Medium Duty (with couplers)',
    materialType: 'BUILD_MATERIAL', qty: 120, unit: 'm', unitCost: 4.85, total: 582,
    source: 'AI_EXTRACTED', sourceDocId: 'doc002', sourcePageRef: 'Sheet 4 — Conduit Routes',
    drawingTaskId: 'tsk008', variationId: null, pooledFromJobId: null,
    complianceFlag: 'COMPLIANT', complianceNote: null, specRef: 'AS/NZS 2053.1',
    complianceCheckedAt: '2026-05-15T09:11:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'DELIVERED', poNumber: 'PO-0043', rfqLineId: null,
    stockStatus: 'ON_SITE', receivedQty: 120, grnIds: ['grn002'],
    warehouseReturnable: true, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Civil', whsFlags: [],
    createdAt: '2026-05-15T09:11:00Z', updatedAt: '2026-05-15T09:11:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j1_004', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'CONC-40MPA', description: 'Concrete Grade 40 MPa (foundation pour)',
    materialType: 'BUILD_MATERIAL', qty: 0.8, unit: 'm³', unitCost: 285.00, total: 228,
    source: 'AI_EXTRACTED', sourceDocId: 'doc002', sourcePageRef: 'Sheet 2 — Foundation Details',
    drawingTaskId: 'tsk002', variationId: null, pooledFromJobId: null,
    complianceFlag: 'COMPLIANT', complianceNote: null, specRef: 'AS 3600',
    complianceCheckedAt: '2026-05-15T09:11:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'DELIVERED', poNumber: 'PO-0043', rfqLineId: null,
    stockStatus: 'ON_SITE', receivedQty: 0.8, grnIds: ['grn002'],
    warehouseReturnable: false, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Civil', whsFlags: [],
    createdAt: '2026-05-15T09:11:00Z', updatedAt: '2026-05-15T09:11:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j1_005', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'UPVC-PIT-600', description: 'UPVC Cable Pit 600×450×600 with cast iron lid',
    materialType: 'BUILD_MATERIAL', qty: 6, unit: 'EA', unitCost: 218.00, total: 1308,
    source: 'AI_EXTRACTED', sourceDocId: 'doc002', sourcePageRef: 'Sheet 3 — Pit Layout',
    drawingTaskId: 'tsk007', variationId: null, pooledFromJobId: null,
    complianceFlag: 'COMPLIANT', complianceNote: null, specRef: 'MRTS50 cl. 7.1',
    complianceCheckedAt: '2026-05-15T09:11:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'DELIVERED', poNumber: 'PO-0043', rfqLineId: null,
    stockStatus: 'ON_SITE', receivedQty: 6, grnIds: ['grn002'],
    warehouseReturnable: false, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Civil', whsFlags: ['CONFINED_SPACE'],
    createdAt: '2026-05-15T09:11:00Z', updatedAt: '2026-05-15T09:11:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j1_006', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'CAB-3C-6MM', description: 'Multi-core Cable 3C+E 6mm² Cu/XLPE/PVC 450/750V',
    materialType: 'BUILD_MATERIAL', qty: 200, unit: 'm', unitCost: 12.40, total: 2480,
    source: 'AI_EXTRACTED', sourceDocId: 'doc001', sourcePageRef: 'Sheet 6',
    drawingTaskId: 'tsk011', variationId: null, pooledFromJobId: null,
    complianceFlag: 'COMPLIANT', complianceNote: null, specRef: 'AS/NZS 5000.1',
    complianceCheckedAt: '2026-05-15T09:08:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'ON_ORDER', poNumber: 'PO-0042', rfqLineId: null,
    stockStatus: 'WAREHOUSE', receivedQty: 0, grnIds: [],
    warehouseReturnable: true, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Electrical', whsFlags: [],
    createdAt: '2026-05-15T09:08:00Z', updatedAt: '2026-05-15T09:08:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j1_007', jobId: 'j1', jobNumber: 'TP-2026-001',
    code: 'PED-PB-ASSY', description: 'Pedestrian Push Button Assembly',
    materialType: 'BUILD_MATERIAL', qty: 4, unit: 'EA', unitCost: 285.00, total: 1140,
    source: 'AI_EXTRACTED', sourceDocId: 'doc001', sourcePageRef: 'Sheet 9',
    drawingTaskId: 'tsk014', variationId: null, pooledFromJobId: null,
    complianceFlag: 'NEEDS_REVIEW',
    complianceNote: 'Confirm unit is TMR-approved model — check QD-TSG-0026 Rev C supplier list',
    specRef: 'QD-TSG-0026 Rev C cl. 6.4',
    complianceCheckedAt: '2026-05-15T09:08:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: false,
    procurementStatus: 'NOT_ORDERED', poNumber: null, rfqLineId: null,
    stockStatus: 'NOT_AVAILABLE', receivedQty: 0, grnIds: [],
    warehouseReturnable: false, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Electrical', whsFlags: [],
    createdAt: '2026-05-15T09:08:00Z', updatedAt: '2026-05-15T09:08:00Z', createdByName: 'AI Agent',
  },
  // ── Job j2 — Pacific Mwy ──────────────────────────────────────
  {
    id: 'cbom_j2_001', jobId: 'j2', jobNumber: 'TP-2026-002',
    code: 'ITS-CTRL-SCATS', description: 'SCATS Traffic Signal Controller',
    materialType: 'BUILD_MATERIAL', qty: 1, unit: 'EA', unitCost: 12500.00, total: 12500,
    source: 'AI_EXTRACTED', sourceDocId: 'doc006', sourcePageRef: 'Sheet 4 — Controller Layout',
    drawingTaskId: null, variationId: null, pooledFromJobId: null,
    complianceFlag: 'NEEDS_REVIEW',
    complianceNote: 'Open RFI: firmware version not confirmed by TMR — do not order until RFI-TP001-002 closed',
    specRef: 'QD-TSG-0026 Rev B cl. 4.2',
    complianceCheckedAt: '2026-04-10T08:09:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: false,
    procurementStatus: 'ON_ORDER', poNumber: 'PO-0049', rfqLineId: null,
    stockStatus: 'IN_TRANSIT', receivedQty: 0, grnIds: [],
    warehouseReturnable: false, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'ITS', whsFlags: [],
    createdAt: '2026-04-10T08:09:00Z', updatedAt: '2026-04-10T08:09:00Z', createdByName: 'AI Agent',
  },
  {
    id: 'cbom_j2_002', jobId: 'j2', jobNumber: 'TP-2026-002',
    code: 'CAB-FO-12SM', description: 'Fibre Optic Cable 12-core Single Mode',
    materialType: 'BUILD_MATERIAL', qty: 300, unit: 'm', unitCost: 12.00, total: 3600,
    source: 'AI_EXTRACTED', sourceDocId: 'doc007', sourcePageRef: 'Section 3.4',
    drawingTaskId: null, variationId: null, pooledFromJobId: null,
    complianceFlag: 'NEEDS_REVIEW',
    complianceNote: 'Pending RFI-TP002-001 closure — confirm fibre vs 4G primary before ordering',
    specRef: 'QD-TSG-0026 Rev B cl. 3.4',
    complianceCheckedAt: '2026-04-10T08:13:00Z', complianceCheckedBy: 'AI Agent',
    complianceVerifiedForThisJob: false,
    procurementStatus: 'NOT_ORDERED', poNumber: null, rfqLineId: null,
    stockStatus: 'NOT_AVAILABLE', receivedQty: 0, grnIds: [],
    warehouseReturnable: true, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'ITS', whsFlags: [],
    createdAt: '2026-04-10T08:13:00Z', updatedAt: '2026-04-10T08:13:00Z', createdByName: 'AI Agent',
  },
  // ── Example of a POOLED_IN line (UPVC conduit excess from j1 ──
  {
    id: 'cbom_j3_001', jobId: 'j3', jobNumber: 'TP-2026-003',
    code: 'UPVC-33MD', description: 'UPVC Conduit 33mm Medium Duty (transferred from TP-2026-001 site excess)',
    materialType: 'BUILD_MATERIAL', qty: 40, unit: 'm', unitCost: 4.85, total: 194,
    source: 'POOLED_IN', sourceDocId: null, sourcePageRef: null,
    drawingTaskId: null, variationId: null, pooledFromJobId: 'j1',
    complianceFlag: 'COMPLIANT',
    complianceNote: 'Re-verified compliant for TP-2026-003 — same AS/NZS 2053.1 spec applies',
    specRef: 'AS/NZS 2053.1',
    complianceCheckedAt: '2026-06-10T14:00:00Z', complianceCheckedBy: 'Sarah Lee',
    complianceVerifiedForThisJob: true,
    procurementStatus: 'DELIVERED', poNumber: null, rfqLineId: null,
    stockStatus: 'ON_SITE', receivedQty: 40, grnIds: [],
    warehouseReturnable: false, returnedQty: 0, returnedAt: null, returnedByName: null, returnNote: null,
    discipline: 'Civil', whsFlags: [],
    createdAt: '2026-06-10T14:00:00Z', updatedAt: '2026-06-10T14:00:00Z', createdByName: 'Sarah Lee',
  },
]

let _seeded = false
function ensureSeeded() {
  if (_seeded || !canStore()) return
  _seeded = true
  if (!window.localStorage.getItem(STORE_KEY)) writeAll(SEED)
}

// ── Public API ───────────────────────────────────────────────────

export function getAllBOMLines(): CanonicalBOMLine[] {
  ensureSeeded()
  return readAll()
}

export function getBOMForJob(jobId: string): CanonicalBOMLine[] {
  return getAllBOMLines().filter(l => l.jobId === jobId)
}

export function getBOMLineById(id: string): CanonicalBOMLine | null {
  return getAllBOMLines().find(l => l.id === id) ?? null
}

export function getBOMLinesByDrawingTask(drawingTaskId: string): CanonicalBOMLine[] {
  return getAllBOMLines().filter(l => l.drawingTaskId === drawingTaskId)
}

export function getBOMLinesByRFQLine(rfqLineId: string): CanonicalBOMLine[] {
  return getAllBOMLines().filter(l => l.rfqLineId === rfqLineId)
}

export function getNonCompliantLines(jobId?: string): CanonicalBOMLine[] {
  const lines = jobId ? getBOMForJob(jobId) : getAllBOMLines()
  return lines.filter(l => l.complianceFlag === 'NON_COMPLIANT' || l.complianceFlag === 'NEEDS_REVIEW' || !l.complianceVerifiedForThisJob)
}

export function getPooledLines(jobId: string): CanonicalBOMLine[] {
  return getBOMForJob(jobId).filter(l => l.source === 'POOLED_IN' || l.source === 'RETURNED')
}

export function getReturnableExcess(jobId: string): CanonicalBOMLine[] {
  return getBOMForJob(jobId).filter(l =>
    l.warehouseReturnable &&
    l.receivedQty !== undefined &&
    l.qty > 0 &&
    (l.receivedQty ?? 0) < l.qty === false // received >= ordered means potential excess
  )
}

export function addBOMLine(line: Omit<CanonicalBOMLine, 'id' | 'createdAt' | 'updatedAt'>): CanonicalBOMLine {
  const all = getAllBOMLines()
  const now = new Date().toISOString()
  const newLine: CanonicalBOMLine = {
    ...line,
    id: `cbom_${Date.now()}`,
    createdAt: now,
    updatedAt: now,
  }
  writeAll([newLine, ...all])
  return newLine
}

export function updateBOMLine(id: string, changes: Partial<CanonicalBOMLine>): CanonicalBOMLine | null {
  const all = getAllBOMLines()
  const idx = all.findIndex(l => l.id === id)
  if (idx === -1) return null
  const updated = { ...all[idx], ...changes, updatedAt: new Date().toISOString() }
  all[idx] = updated
  writeAll(all)
  return updated
}

export function markComplianceVerified(
  id: string,
  flag: BOMComplianceFlag,
  note: string | null,
  verifiedByName: string,
): CanonicalBOMLine | null {
  return updateBOMLine(id, {
    complianceFlag: flag,
    complianceNote: note,
    complianceCheckedAt: new Date().toISOString(),
    complianceCheckedBy: verifiedByName,
    complianceVerifiedForThisJob: flag !== 'NON_COMPLIANT' && flag !== 'NEEDS_REVIEW',
  })
}

export function linkBOMLineToRFQ(bomLineId: string, rfqLineId: string): CanonicalBOMLine | null {
  return updateBOMLine(bomLineId, { rfqLineId })
}

export function linkBOMLineToPO(bomLineId: string, poNumber: string, status: BOMProcurementStatus): CanonicalBOMLine | null {
  return updateBOMLine(bomLineId, { poNumber, procurementStatus: status })
}

export function recordGRNReceived(bomLineId: string, grnId: string, receivedQty: number): CanonicalBOMLine | null {
  const line = getBOMLineById(bomLineId)
  if (!line) return null
  const newGrnIds = [...(line.grnIds ?? []), grnId]
  const newReceivedQty = (line.receivedQty ?? 0) + receivedQty
  const stockStatus: BOMStockStatus =
    newReceivedQty >= line.qty ? 'ON_SITE' :
    newReceivedQty > 0 ? 'PARTIAL_ON_SITE' : line.stockStatus
  const procurementStatus: BOMProcurementStatus =
    newReceivedQty >= line.qty ? 'DELIVERED' : 'PARTIAL_RECEIVED'
  return updateBOMLine(bomLineId, { grnIds: newGrnIds, receivedQty: newReceivedQty, stockStatus, procurementStatus })
}

/** Pool a BOM line quantity from a donor job into a recipient job's BOM */
export function poolMaterialFromJob(
  donorJobId: string,
  donorJobNumber: string,
  donorBomLineId: string,
  recipientJobId: string,
  recipientJobNumber: string,
  qty: number,
  transferredByName: string,
): CanonicalBOMLine | null {
  const donor = getBOMLineById(donorBomLineId)
  if (!donor) return null
  const now = new Date().toISOString()
  const newLine: CanonicalBOMLine = {
    ...donor,
    id: `cbom_${Date.now()}`,
    jobId: recipientJobId,
    jobNumber: recipientJobNumber,
    qty,
    total: qty * donor.unitCost,
    source: 'POOLED_IN',
    pooledFromJobId: donorJobId,
    // CRITICAL: must re-verify compliance against recipient job's specs
    complianceVerifiedForThisJob: false,
    complianceFlag: 'NEEDS_REVIEW',
    complianceNote: `Transferred from job ${donorJobNumber} — compliance must be re-verified against this job's client specifications`,
    complianceCheckedAt: null,
    complianceCheckedBy: null,
    procurementStatus: 'DELIVERED',
    stockStatus: 'ON_SITE',
    receivedQty: qty,
    grnIds: [],
    createdAt: now,
    updatedAt: now,
    createdByName: transferredByName,
  }
  writeAll([newLine, ...getAllBOMLines()])
  return newLine
}

/** Record a site-excess return to the warehouse */
export function returnMaterialToWarehouse(
  bomLineId: string,
  returnQty: number,
  returnedByName: string,
  note: string,
): CanonicalBOMLine | null {
  return updateBOMLine(bomLineId, {
    returnedQty: (getBOMLineById(bomLineId)?.returnedQty ?? 0) + returnQty,
    returnedAt: new Date().toISOString(),
    returnedByName,
    returnNote: note,
    stockStatus: 'WAREHOUSE',
  })
}

// ── Computed readiness ───────────────────────────────────────────

export type MaterialsReadiness = {
  ready: boolean
  partial: boolean
  blockedByCompliance: boolean
  lines: {
    line: CanonicalBOMLine
    ready: boolean
    reason?: string
  }[]
}

/** For a given DrawingTask, compute whether its materials are available and verified */
export function computeMaterialsReadiness(drawingTaskId: string): MaterialsReadiness {
  const lines = getBOMLinesByDrawingTask(drawingTaskId)
  if (lines.length === 0) return { ready: true, partial: false, blockedByCompliance: false, lines: [] }

  const evaluated = lines.map(l => {
    if (!l.complianceVerifiedForThisJob || l.complianceFlag === 'NON_COMPLIANT') {
      return { line: l, ready: false, reason: `Compliance not verified (${l.complianceFlag})` }
    }
    if (l.complianceFlag === 'NEEDS_REVIEW') {
      return { line: l, ready: false, reason: 'Compliance needs review before use' }
    }
    if (l.stockStatus === 'ON_SITE') return { line: l, ready: true }
    if (l.stockStatus === 'PARTIAL_ON_SITE') return { line: l, ready: false, reason: 'Only partial quantity on site' }
    if (l.stockStatus === 'WAREHOUSE') return { line: l, ready: false, reason: 'In warehouse — not yet delivered to site' }
    if (l.stockStatus === 'IN_TRANSIT') return { line: l, ready: false, reason: 'In transit — not yet received' }
    return { line: l, ready: false, reason: 'Not available' }
  })

  const allReady = evaluated.every(e => e.ready)
  const anyReady = evaluated.some(e => e.ready)
  const anyComplianceBlock = evaluated.some(e => !e.ready && (e.reason?.toLowerCase().includes('compliance')))

  return {
    ready: allReady,
    partial: !allReady && anyReady,
    blockedByCompliance: anyComplianceBlock,
    lines: evaluated,
  }
}

// ── Compliance gate for RFQ ───────────────────────────────────────

export type RFQComplianceGate = {
  canSend: boolean
  blockingLines: { bomLineId: string; description: string; flag: BOMComplianceFlag; reason: string }[]
  warningLines: { bomLineId: string; description: string; note: string }[]
}

/** Check all BOM lines linked to an RFQ's line items before allowing send */
export function checkRFQComplianceGate(rfqLineItems: { id: string; bomLineId?: string }[]): RFQComplianceGate {
  const blockingLines: RFQComplianceGate['blockingLines'] = []
  const warningLines: RFQComplianceGate['warningLines'] = []

  rfqLineItems.forEach(item => {
    if (!item.bomLineId) return
    const line = getBOMLineById(item.bomLineId)
    if (!line) return
    if (line.complianceFlag === 'NON_COMPLIANT') {
      blockingLines.push({ bomLineId: line.id, description: line.description, flag: 'NON_COMPLIANT', reason: line.complianceNote ?? 'Non-compliant with client specifications' })
    } else if (line.complianceFlag === 'NEEDS_REVIEW' || !line.complianceVerifiedForThisJob) {
      blockingLines.push({ bomLineId: line.id, description: line.description, flag: 'NEEDS_REVIEW', reason: line.complianceNote ?? 'Compliance review required before RFQ can be sent' })
    } else if (line.complianceFlag === 'PENDING') {
      warningLines.push({ bomLineId: line.id, description: line.description, note: 'Compliance check pending — RFQ can be sent but PM should confirm before award' })
    }
  })

  return { canSend: blockingLines.length === 0, blockingLines, warningLines }
}

// ── SOURCE_META for display ──────────────────────────────────────
export const BOM_SOURCE_META: Record<BOMSource, { label: string; bg: string; text: string; icon: string }> = {
  AI_EXTRACTED: { label: 'AI Extracted',    bg: 'bg-purple-100', text: 'text-purple-700', icon: '🤖' },
  MANUAL:       { label: 'Manual',          bg: 'bg-gray-100',   text: 'text-gray-600',   icon: '✏️' },
  VARIATION:    { label: 'Variation',       bg: 'bg-amber-100',  text: 'text-amber-700',  icon: '⚡' },
  POOLED_IN:    { label: 'Pooled from job', bg: 'bg-blue-100',   text: 'text-blue-700',   icon: '↗' },
  RETURNED:     { label: 'Returned stock',  bg: 'bg-teal-100',   text: 'text-teal-700',   icon: '↩' },
}

export const BOM_COMPLIANCE_META: Record<BOMComplianceFlag, { label: string; bg: string; text: string; border: string }> = {
  COMPLIANT:     { label: 'Compliant',      bg: 'bg-green-100',  text: 'text-green-700', border: 'border-green-300' },
  NEEDS_REVIEW:  { label: 'Needs review',   bg: 'bg-amber-100',  text: 'text-amber-700', border: 'border-amber-300' },
  NON_COMPLIANT: { label: 'Non-compliant',  bg: 'bg-red-100',    text: 'text-red-700',   border: 'border-red-400' },
  PENDING:       { label: 'Check pending',  bg: 'bg-gray-100',   text: 'text-gray-500',  border: 'border-gray-300' },
}

export const BOM_STOCK_META: Record<BOMStockStatus, { label: string; bg: string; text: string }> = {
  ON_SITE:         { label: 'On site',        bg: 'bg-green-100',  text: 'text-green-700' },
  PARTIAL_ON_SITE: { label: 'Partial on site',bg: 'bg-amber-100',  text: 'text-amber-700' },
  WAREHOUSE:       { label: 'Warehouse',      bg: 'bg-blue-100',   text: 'text-blue-700' },
  IN_TRANSIT:      { label: 'In transit',     bg: 'bg-purple-100', text: 'text-purple-700' },
  NOT_AVAILABLE:   { label: 'Not available',  bg: 'bg-red-100',    text: 'text-red-700' },
}

export const BOM_PROCUREMENT_META: Record<BOMProcurementStatus, { label: string; bg: string; text: string }> = {
  NOT_ORDERED:      { label: 'Not ordered',     bg: 'bg-gray-100',   text: 'text-gray-500' },
  PENDING_APPROVAL: { label: 'Pending approval', bg: 'bg-amber-100',  text: 'text-amber-700' },
  ON_ORDER:         { label: 'On order',         bg: 'bg-blue-100',   text: 'text-blue-700' },
  PARTIAL_RECEIVED: { label: 'Partial received', bg: 'bg-amber-100',  text: 'text-amber-700' },
  DELIVERED:        { label: 'Delivered',        bg: 'bg-green-100',  text: 'text-green-700' },
}
